/*
 * Project: bellcloud
 * File: apps/frontend/src/components/device/connectDevice/ConnectDevice.tsx
 * Description: Complete connection device component combining DeviceImage, ConnectionState, and ConnectionButtons
 * Uses global connection store - no props needed
 */

import React from 'react';
import styled from '@emotion/styled';
import { DeviceType } from '@packages/types/device/common/deviceTypes';
import type { UiConnectedConnectionType } from '@/types/connection';
import { DeviceImage } from './DeviceImage';
import { ConnectionStatusBar } from './ConnectionStatusBar';
import { ConnectionButtons } from './ConnectionButtons';

interface ConnectDeviceProps {
  /** Device type - optional, defaults to LITE */
  deviceType?: DeviceType;
  /** Enable cradle connection - optional, defaults to true */
  cradleAble?: boolean;
  /** Show device image - optional, defaults to true */
  deviceImage?: boolean;
  /** Show detailed device and cloud information - optional, defaults to false */
  showDetailInfo?: boolean;
  /** Callback when device is loaded with deviceInfo and deviceConfig */
  onDeviceLoaded?: (deviceInfo: any, deviceConfig: any) => void;
  /** Callback when connection type changes */
  onConnectionChange?: (connectionType: UiConnectedConnectionType) => void;
  /** Callback when device is disconnected */
  onDisconnected?: () => void;
}

/**
 * ConnectDevice Component
 *
 * Complete connection interface:
 * - Left: Device image (114x114px circular)
 * - Right: Connection state bar + Action buttons
 * - Optional: Detailed device and cloud information below
 * - Total size: 558px × 114px (+ detail info if enabled)
 * - Gap between sections: 11px
 * - Supports callbacks for parent components to receive device data
 */
export const ConnectDevice: React.FC<ConnectDeviceProps> = ({
  deviceType = DeviceType.LITE,
  cradleAble = true,
  deviceImage = true,
  showDetailInfo = false,
  onDeviceLoaded,
  onConnectionChange,
  onDisconnected,
}) => {
  return (
    <Wrapper>
      <Container $showImage={deviceImage}>
        {/* Left: Device Image */}
        {deviceImage && (
          <ImageSection>
            <DeviceImage deviceType={deviceType} />
          </ImageSection>
        )}

        {/* Right: Connection State + Buttons */}
        <ControlSection $fullWidth={!deviceImage}>
          <ConnectionStatusBar showDetailInfo={showDetailInfo} />
          <ConnectionButtons
            cradleAble={cradleAble}
            onDeviceLoaded={onDeviceLoaded}
            onConnectionChange={onConnectionChange}
            onDisconnected={onDisconnected}
          />
        </ControlSection>
      </Container>
    </Wrapper>
  );
};

// ==================== Styled Components ====================

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]}px;
  width: 100%;
`;

const Container = styled.div<{ $showImage: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 0px;
  gap: ${props => (props.$showImage ? '11px' : '0px')};
  width: 100%;
  height: auto;
  min-height: 114px;
`;

const ImageSection = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 0px;
  gap: 10px;
  width: 163px;
  height: 114px;
  flex: none;
  order: 0;
  flex-grow: 0;
`;

const ControlSection = styled.div<{ $fullWidth: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 12px;
  flex: 1;
  height: auto;
  order: 1;
`;
